import posthog from 'posthog-js';
import { getEnv } from '@/lib/utils/env';
import { IS_PROD } from '@/lib/utils/env/is-prod';
import { createBrowserBeforeSend, runPreCapture } from './common';
import type {
  FrontendIdentifyId,
  FrontendIdentifyProperties,
  FrontendInitOptions,
  PostHogIds,
} from './types';

const RUNTIME = 'frontend';
const ORGANIZATION_GROUP_TYPE = 'organization';
const ORGANIZATION_SESSION_PROPERTIES = [
  'organization_id',
  'organization_name',
  'organization_plan',
  'organization_role',
] as const;

/** Organization facts attached to PostHog groups and future browser events. */
export interface PostHogOrganizationContext {
  /** Immutable internal organization UUID. */
  organizationId: string;
  /** Current organization display name. */
  organizationName: string;
  /** Stable application plan tier. */
  organizationPlan: 'free' | 'pro' | 'business';
  /** Current user's role inside this organization. */
  organizationRole: string;
}

/** Result metadata supplied when PostHog finishes loading feature flags. */
export interface PostHogFeatureFlagsLoadContext {
  /** True when the most recent remote flag request failed. */
  errorsLoading: boolean;
}

/** Options supported by boolean feature-flag lookups. */
export interface PostHogFeatureFlagOptions {
  /** Whether the lookup should capture PostHog's exposure event. */
  send_event?: boolean;
  /** Whether the lookup must ignore values restored only from persistence. */
  fresh?: boolean;
}

type FeatureFlagsSubscriber = (context: PostHogFeatureFlagsLoadContext) => void;

let isPostHogInitialized = false;
let desiredOrganizationContext: PostHogOrganizationContext | null = null;
let appliedOrganizationContext: PostHogOrganizationContext | null = null;
let hasAppliedOrganizationState = false;
let reloadFlagsAfterInitialization = false;
let featureFlagsSdkUnsubscribe: (() => void) | null = null;
const featureFlagsSubscribers = new Set<FeatureFlagsSubscriber>();

const deploymentEnvironment = IS_PROD ? 'production' : 'staging';

/** Returns true when two complete organization contexts are identical. */
const organizationContextsEqual = (
  left: PostHogOrganizationContext | null,
  right: PostHogOrganizationContext | null,
): boolean => (
  left?.organizationId === right?.organizationId
  && left?.organizationName === right?.organizationName
  && left?.organizationPlan === right?.organizationPlan
  && left?.organizationRole === right?.organizationRole
);

/** Returns true when the group profile fields are identical. */
const organizationGroupPropertiesEqual = (
  left: PostHogOrganizationContext | null,
  right: PostHogOrganizationContext,
): boolean => (
  left?.organizationId === right.organizationId
  && left?.organizationName === right.organizationName
  && left?.organizationPlan === right.organizationPlan
);

/** Applies the latest desired organization only after the SDK is initialized. */
const applyDesiredOrganizationContext = (): boolean => {
  if (!isPostHogInitialized) return false;
  if (
    hasAppliedOrganizationState
    && organizationContextsEqual(appliedOrganizationContext, desiredOrganizationContext)
  ) {
    return false;
  }

  if (!desiredOrganizationContext) {
    for (const property of ORGANIZATION_SESSION_PROPERTIES) {
      posthog.unregister_for_session(property);
    }
    posthog.resetGroups();
    appliedOrganizationContext = null;
    hasAppliedOrganizationState = true;
    return true;
  }

  const nextContext = desiredOrganizationContext;
  posthog.register_for_session({
    deployment_environment: deploymentEnvironment,
    organization_id: nextContext.organizationId,
    organization_name: nextContext.organizationName,
    organization_plan: nextContext.organizationPlan,
    organization_role: nextContext.organizationRole,
  });

  if (!organizationGroupPropertiesEqual(appliedOrganizationContext, nextContext)) {
    posthog.group(ORGANIZATION_GROUP_TYPE, nextContext.organizationId, {
      organization_id: nextContext.organizationId,
      organization_name: nextContext.organizationName,
      organization_plan: nextContext.organizationPlan,
    });
  } else {
    // `group()` reloads flags after group properties change. A role-only
    // change needs an explicit reload after its session context is updated.
    posthog.reloadFeatureFlags();
  }

  appliedOrganizationContext = nextContext;
  hasAppliedOrganizationState = true;
  return true;
};

/** Binds the shared SDK feature-ready listener once initialization completes. */
const bindFeatureFlagsSubscribers = () => {
  if (
    !isPostHogInitialized
    || featureFlagsSdkUnsubscribe
    || featureFlagsSubscribers.size === 0
  ) {
    return;
  }

  featureFlagsSdkUnsubscribe = posthog.onFeatureFlags(
    (_flags, _variants, context) => {
      const result = { errorsLoading: Boolean(context?.errorsLoading) };
      for (const subscriber of featureFlagsSubscribers) {
        subscriber(result);
      }
    },
  );
};

/**
 * Frontend PostHog helper.
 * Capture only terminal failures and unexpected recovered failures with `captureException`.
 * Skip expected control-flow catches used for normal behavior.
 */
export const initPostHog = (options?: FrontendInitOptions) => {
  const apiKey = getEnv('VITE_POSTHOG_KEY', false);
  if (!apiKey) return;

  const apiHost = getEnv('VITE_POSTHOG_HOST', false, 'https://eu.i.posthog.com');
  posthog.init(apiKey, {
    api_host: apiHost,
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    secure_cookie: true,
    person_profiles: 'always',
    debug: import.meta.env.DEV,
    loaded: (instance) => {
      isPostHogInitialized = true;
      bindFeatureFlagsSubscribers();
      instance.register_for_session({
        deployment_environment: deploymentEnvironment,
      });
      const organizationReloadedFlags = applyDesiredOrganizationContext();
      if (reloadFlagsAfterInitialization && !organizationReloadedFlags) {
        instance.reloadFeatureFlags();
      }
      reloadFlagsAfterInitialization = false;
      if (import.meta.env.DEV) instance.debug();
    },
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    enable_recording_console_log: true,
    session_recording: {
      recordCrossOriginIframes: true,
    },
    capture_exceptions: true,
    autocapture: true,
    enable_heatmaps: true,
    capture_performance: true,
    before_send: createBrowserBeforeSend(RUNTIME, options?.beforeSend),
  });
};

export const getPostHogSessionId = (): string | null => {
  try {
    return posthog.get_session_id() || null;
  } catch {
    return null;
  }
};

export const getPostHogDistinctId = (): string | null => {
  try {
    return posthog.get_distinct_id() || null;
  } catch {
    return null;
  }
};

export const getPostHogIds = (): PostHogIds => ({
  sessionId: getPostHogSessionId(),
  distinctId: getPostHogDistinctId(),
});

export const identifyUser = (
  userId: FrontendIdentifyId,
  properties?: FrontendIdentifyProperties,
) => {
  posthog.identify(userId, properties);
};

export const logoutUser = () => {
  desiredOrganizationContext = null;
  appliedOrganizationContext = null;
  hasAppliedOrganizationState = isPostHogInitialized;
  reloadFlagsAfterInitialization = false;
  posthog.reset();
  if (isPostHogInitialized) {
    posthog.register_for_session({
      deployment_environment: deploymentEnvironment,
    });
  }
};

/**
 * Associates future events and feature-flag evaluation with the active
 * organization. Calls made before SDK initialization retain only the latest
 * context and apply it from PostHog's `loaded` callback.
 */
export const syncPostHogOrganization = (context: PostHogOrganizationContext) => {
  desiredOrganizationContext = context;
  applyDesiredOrganizationContext();
};

/**
 * Removes organization event properties before resetting PostHog groups.
 * `resetGroups()` then reloads flags against the cleared context.
 */
export const clearPostHogOrganization = () => {
  desiredOrganizationContext = null;
  applyDesiredOrganizationContext();
};

/** Registers a listener for completed or failed remote feature-flag loads. */
export const onPostHogFeatureFlags = (
  callback: FeatureFlagsSubscriber,
): (() => void) => {
  featureFlagsSubscribers.add(callback);
  bindFeatureFlagsSubscribers();

  return () => {
    featureFlagsSubscribers.delete(callback);
    if (featureFlagsSubscribers.size === 0 && featureFlagsSdkUnsubscribe) {
      featureFlagsSdkUnsubscribe();
      featureFlagsSdkUnsubscribe = null;
    }
  };
};

/** Registers a listener that fires immediately before flag values reload. */
export const onPostHogFeatureFlagsReloading = (
  callback: () => void,
): (() => void) => posthog.on('featureFlagsReloading', callback);

/** Returns a boolean flag value, or undefined before SDK initialization. */
export const isPostHogFeatureEnabled = (
  flagKey: string,
  options?: PostHogFeatureFlagOptions,
): boolean | undefined => (
  isPostHogInitialized
    ? posthog.isFeatureEnabled(flagKey, options)
    : undefined
);

/** Reloads feature flags now, or once immediately after SDK initialization. */
export const reloadPostHogFeatureFlags = () => {
  if (!isPostHogInitialized) {
    reloadFlagsAfterInitialization = true;
    return;
  }
  posthog.reloadFeatureFlags();
};

export const captureEvent = (eventName: string, properties?: Record<string, unknown>) => {
  try {
    const prepared = runPreCapture({
      kind: 'event',
      runtime: RUNTIME,
      event: eventName,
      distinctId: getPostHogDistinctId() || 'frontend:anonymous',
      properties: {
        ...(properties || {}),
        environment: RUNTIME,
      },
    });
    if (!prepared) return;
    posthog.capture(prepared.event, prepared.properties);
  } catch (error) {
    console.error('[PostHog] captureEvent error:', error);
  }
};

export const captureException = (
  error: Error | string,
  context: string,
  properties?: Record<string, unknown>,
) => {
  try {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const prepared = runPreCapture({
      kind: 'exception',
      runtime: RUNTIME,
      event: '$exception',
      context,
      distinctId: getPostHogDistinctId() || 'frontend:anonymous',
      properties: {
        context,
        environment: RUNTIME,
        ...(properties || {}),
      },
    });
    if (!prepared) return;
    posthog.captureException(errorObj, prepared.properties);
  } catch (captureFailure) {
    console.error('[PostHog] captureException error:', captureFailure);
  }
};
